<script setup lang="ts">
import { computed } from "vue"
import { DropdownButton, DropdownItem } from "@hantera/design-system"
import type { AppSettingDefinitionInfo } from "@/backend/apps.types"

const props = defineProps<{
  modelValue: unknown
  definition: AppSettingDefinitionInfo
  settingKey: string
  readonly?: boolean
  hasStoredValue?: boolean
}>()

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void
}>()

interface SelectOption {
  value: string
  label: string | Record<string, string>
}

function resolveOptionLabel(label: string | Record<string, string> | undefined | null): string {
  if (label == null) return ""
  if (typeof label === "string") return label
  const userLang = document.documentElement.lang || "en"
  return label[userLang] ?? label["default"] ?? Object.values(label)[0] ?? ""
}

const options = computed<SelectOption[]>(() => {
  const raw = props.definition.editor?.options
  if (!Array.isArray(raw)) return []
  // Options may use the object form ({ value, label }) or a plain-string
  // shorthand where the string is both the value and the label.
  return raw.map((opt) =>
    typeof opt === "string"
      ? { value: opt, label: opt }
      : (opt as SelectOption)
  )
})


const resolvedOptions = computed(() =>
  options.value.map((opt) => ({
    value: opt.value,
    label: resolveOptionLabel(opt.label),
  }))
)

const displayLabel = computed(() => {
  if (props.modelValue === undefined && props.hasStoredValue && props.definition.secret) {
    return "Secret value stored"
  }
  const current = resolvedOptions.value.find((o) => o.value === props.modelValue)
  return current?.label ?? (props.modelValue as string) ?? "Not set"
})
</script>

<template>
  <DropdownButton
    :label="displayLabel"
    :disabled="readonly ?? false"
  >
    <DropdownItem
      v-if="definition.secret && hasStoredValue && modelValue === undefined"
      disabled
    >
      Secret value stored
    </DropdownItem>
    <DropdownItem
      v-for="option in resolvedOptions"
      :key="option.value"
      @click="emit('update:modelValue', option.value)"
    >
      {{ option.label }}
    </DropdownItem>
  </DropdownButton>
</template>
